import { dedupeList, type Nullable, type OptionalString } from '@amzn/elevate-data-types';

import { amplifyFetch } from '@/common/data-provider';
import { ElevateDateTime } from '@/common/date-time-helpers';
import { InterviewerView } from '@/common/models';
import { cleanPermissionGroup, sort } from '@/common/utility';
import { processEventAvailability } from '@/components/common/event/data-helpers';
import {
  getProfileCalibrations,
  getProfileCalibrationsByConfig,
  getProfileClassic,
  getProfilePlatform,
  getProfilesByOrgAndRole,
  getProfilesWithCalibration,
  listProfileAvailabilityByOrg,
  listProfilesByManager,
  listProfilesByShepherd,
  listTranscripts,
} from '@/graphql/queries';
import {
  gql,
  type ModelProfileCalibrationFilterInput,
  type ModelProfileOrganizationV2FilterInput,
  OrganizationStatus,
  ProfileStatus,
  Roles,
} from '@/graphql/types';
import type { CalibrationState, InterviewerProfileItem, ProfileSelectOption, TranscriptItem, UserProfile } from '@/models';
import featureFlags from '@/utilities/feature-flags';

async function getOrgInterviewersByManager(managerAlias: OptionalString) {
  if (!managerAlias) return [];
  const results = await amplifyFetch({
    document: gql(listProfilesByManager),
    variables: { managerAlias },
    onFetchNextToken: (v) => v.listProfilesByManager.nextToken || null,
  });
  return results.flatMap((result) => result.listProfilesByManager.items ?? []);
}

async function getOrgInterviewersByShepherd(orgID: string, id: OptionalString) {
  if (!id) return [];
  const results = await amplifyFetch({
    document: gql(listProfilesByShepherd),
    variables: { id },
    onFetchNextToken: (v) => v.getProfile.cohortMembers?.nextToken || null,
  });
  return results
    .flatMap((result) => result.getProfile.cohortMembers?.items ?? [])
    .map((item) => item.profile)
    .filter(
      (profile): profile is NonNullable<typeof profile> =>
        !!profile && !!profile.orgs?.items.some((org) => org.orgID === orgID)
    );
}

async function getOrgInterviewersByOrg(orgID: string) {
  const results = await amplifyFetch({
    document: gql(getProfilesWithCalibration),
    variables: { orgID },
    onFetchNextToken: (v) => v.listProfilesByOrgV2.nextToken || null,
  });
  return results
    .flatMap((result) => result.listProfilesByOrgV2.items ?? [])
    .map((item) => item.profile)
    .filter((profile) => profile !== null);
}

export async function getOrgInterviewers(
  orgId: OptionalString,
  view: InterviewerView,
  profileIdOrAlias?: string
): Promise<InterviewerProfileItem[]> {
  if (!orgId) return [];
  let interviewerProfiles: InterviewerProfileItem[];
  switch (view) {
    case InterviewerView.CALIBRATION_SHEPHERD:
      interviewerProfiles = await getOrgInterviewersByShepherd(orgId, profileIdOrAlias);
      break;
    case InterviewerView.MANAGER:
      interviewerProfiles = await getOrgInterviewersByManager(profileIdOrAlias);
      break;
    case InterviewerView.ORGANIZATION:
      interviewerProfiles = await getOrgInterviewersByOrg(orgId);
      break;
    default:
      interviewerProfiles = [];
  }
  return interviewerProfiles
    .filter((profile) => profile.status === ProfileStatus.ACTIVE)
    .map((profile) => ({
      ...profile,
      calibrations: { items: profile.calibrations?.items ?? [] },
    }));
}

async function getUserProfileClassic(id: string) {
  const document = gql(getProfileClassic);
  const result = await amplifyFetch({ document, variables: { id } });
  return result.getProfile;
}

async function getUserProfilePlatform(id: string) {
  const document = gql(getProfilePlatform);
  const result = await amplifyFetch({ document, variables: { id } });
  return result.getProfile;
}

export async function getUserProfile(profileId: OptionalString): Promise<UserProfile | null> {
  if (!profileId) return null;
  let profile: UserProfile | null;
  if (featureFlags.PERMISSION_GROUP_MODEL.value === 'classic') {
    const result = await getUserProfileClassic(profileId);
    profile = result
      ? {
          ...result,
          orgs: (result.orgs?.items ?? [])
            .map((item) => item.organization)
            .filter((org) => !!org)
            .map((org) => ({
              ...org,
              status: org.status ?? OrganizationStatus.ACTIVE,
              roleGroups: org.roleLDAPGroups?.items ?? [],
            })),
        }
      : null;
  } else {
    const result = await getUserProfilePlatform(profileId);
    profile = result
      ? {
          ...result,
          orgs: (result.orgs?.items ?? [])
            .map((item) => item.organization)
            .filter((org) => !!org)
            .map((org) => ({
              ...org,
              status: org.status ?? OrganizationStatus.ACTIVE,
              roleGroups: (org.roleGroups?.items ?? []).map((i) => ({ ...i, group: i.teamId })),
            })),
        }
      : null;
  }
  if (!profile) return null;
  return {
    ...profile,
    orgs: (profile?.orgs ?? []).map((org) => ({
      ...org,
      roleGroups: org.roleGroups.map((rg) => ({ ...rg, group: cleanPermissionGroup(rg.group) })),
    })),
  };
}

export async function getProfileCalibrationState(
  profileId: OptionalString,
  orgId: OptionalString,
  calibrationId?: OptionalString
): Promise<CalibrationState[]> {
  if (!profileId) return [];
  const document = gql(getProfileCalibrations);
  const results = await amplifyFetch({
    document,
    variables: {
      id: profileId,
      calibrationsFilter: calibrationId ? { calibrationId: { eq: calibrationId } } : null,
    },
    onFetchNextToken: (v) => v.getProfile.calibrations?.nextToken || null,
  });

  return results
    .flatMap((result) => result.getProfile.calibrations?.items ?? [])
    .filter((item) => item.calibration?.orgID === orgId)
    .map((item) => ({
      status: item.status,
      calibratedDate: item.calibratedAt,
      changeJustification: item.changeJustification,
      shadowCount: item.shadowCount ?? 0,
      reverseShadowCount: item.reverseShadowCount ?? 0,
      independentCount: item.independentCount ?? 0,
      shepherdReviewCount: item.reviewCount ?? 0,
      lastModifiedBy: item.lastModifiedBy,
      calibrationConfigId: item.calibrationId,
    }));
}

export async function getOrgProfilesByRole(
  orgId: OptionalString,
  includeRoles?: Nullable<Roles[]>,
  excludeRoles?: Nullable<Roles[]>
): Promise<ProfileSelectOption[]> {
  if (!orgId) return [];
  let filter: Nullable<ModelProfileOrganizationV2FilterInput>;
  let includeFilters: ModelProfileOrganizationV2FilterInput[] = [];
  let excludeFilters: ModelProfileOrganizationV2FilterInput[] = [];
  if (includeRoles?.length) {
    includeFilters = includeRoles.map((item) => ({ role: { contains: item } }));
  }
  if (excludeRoles?.length) {
    excludeFilters = excludeRoles.map((item) => ({ role: { notContains: item } }));
  }
  if (includeFilters.length) {
    filter = { or: includeFilters };
  }
  if (excludeFilters.length) {
    filter = { or: excludeFilters };
  }
  if (excludeFilters.length && includeFilters.length) {
    filter = { and: [{ or: excludeFilters }, { or: includeFilters }] };
  }
  const document = gql(getProfilesByOrgAndRole);
  const results = await amplifyFetch({
    document,
    variables: { filter, orgID: orgId },
    onFetchNextToken: (v) => v.listProfilesByOrgV2.nextToken || null,
  });
  const allOptions: ProfileSelectOption[] = results
    .flatMap((result) => result.listProfilesByOrgV2.items ?? [])
    .filter((item) => item.profile?.status === ProfileStatus.ACTIVE)
    .map((item) =>
      item.profile && item.organization
        ? {
            value: item.profile.id,
            label: item.profile.name ?? undefined,
            labelTag: item.profile.title ?? undefined,
            description: item.profile.email ?? `${item.profile.alias}@amazon.com`,
            orgID: item.orgID,
            orgName: item.organization.name,
            alias: item.profile.alias,
            email: item.profile.email ?? `${item.profile.alias}@amazon.com`,
            title: item.profile.title,
          }
        : null
    )
    .filter((item) => item !== null);

  return dedupeList(allOptions, 'value').toSorted((a, b) => sort(a.label, b.label));
}

export async function getOrgInterviewerProfileOptions(
  orgId: string,
  calibrationId?: OptionalString,
  calStatusFilter?: Nullable<ModelProfileCalibrationFilterInput>
): Promise<ProfileSelectOption[]> {
  if (!calibrationId) {
    return getOrgProfilesByRole(orgId);
  }
  const document = gql(getProfileCalibrationsByConfig);
  const results = await amplifyFetch({
    document,
    variables: { calibrationId, filter: calStatusFilter || null },
    onFetchNextToken: (v) => v.getOrgLoopPSConfigurations.calibrations?.nextToken || null,
  });

  const org = results.at(0)?.getOrgLoopPSConfigurations.org;
  if (!org) return [];

  return results
    .flatMap((result) => result.getOrgLoopPSConfigurations.calibrations?.items ?? [])
    .filter((item) => item.profile?.status === ProfileStatus.ACTIVE)
    .map((item) =>
      item.profile
        ? {
            value: item.profile.id,
            label: item.profile.name ?? undefined,
            labelTag: item.profile.title ?? undefined,
            description: item.profile.email ?? `${item.profile.alias}@amazon.com`,
            calibrationStatus: item.status,
            orgID: org.id,
            orgName: org.name,
            alias: item.profile.alias,
            email: item.profile.email ?? `${item.profile.alias}@amazon.com`,
            title: item.profile.title,
          }
        : null
    )
    .filter((item) => item !== null);
}

export async function getOrgProfileAvailabilities(orgId: string, fallbackTimezoneId?: string) {
  const document = gql(listProfileAvailabilityByOrg);
  const results = await amplifyFetch({
    document,
    variables: { orgID: orgId },
    onFetchNextToken: (v) => v.listProfilesByOrgV2?.nextToken || null,
  });
  return results
    .flatMap((result) => result.listProfilesByOrgV2.items ?? [])
    .map((item) => item.profile)
    .filter((profile) => profile !== null)
    .filter((profile) => profile.status === ProfileStatus.ACTIVE && !!profile.availabilities?.items.length)
    .map((profile) => ({
      ...profile,
      availabilities: (profile.availabilities?.items ?? []).map((i) =>
        processEventAvailability(i, { ...profile, timezoneId: profile.preferredTimezoneId ?? fallbackTimezoneId })
      ),
      calibrations: profile.calibrations?.items ?? [],
    }));
}

export async function getTrainingTranscripts(
  learnUserId: OptionalString,
  trainingIds: Set<string>,
  timezoneId: OptionalString
): Promise<Map<string, TranscriptItem>> {
  if (!(learnUserId && trainingIds.size)) return new Map();
  const result = await amplifyFetch({
    document: gql(listTranscripts),
    variables: { learnUserId, trainingIds: [...trainingIds] },
  });
  const transcripts = (result.listLearnAndManagedTranscripts ?? []).map((item) => ({
    ...item,
    completedAt: ElevateDateTime.fromUTC(item.completedAt, timezoneId).displayDate,
    failedAt: ElevateDateTime.fromUTC(item.failedAt, timezoneId).displayDate,
  }));
  const trainingTranscripts = new Map<string, TranscriptItem>();
  for (const transcript of transcripts ?? []) {
    if (!trainingIds.has(transcript.trainingId)) continue;
    const filterVersion = Number.parseFloat(transcript.trainingVersion ?? '0');
    const existing = trainingTranscripts.get(transcript.trainingId);
    const isNewerVersion = (existing?.filterVersion ?? 0) < filterVersion;
    const isSameVersion = (existing?.filterVersion ?? 0) === filterVersion;
    const isLaterDate = (existing?.completedAt ?? '') < (transcript.completedAt ?? '');
    if (!existing || isNewerVersion || (isSameVersion && isLaterDate)) {
      trainingTranscripts.set(transcript.trainingId, { ...transcript, filterVersion });
    }
  }
  return trainingTranscripts;
}
